import React from 'react';
import { Neo, NeoGrid } from '@singularsystems/neo-react';
import { observer } from 'mobx-react';
import DateCyclesVM from '../DateCyclesVM';
import DateCycle from 'domain-models/Dividends/DateCycle';
import { DayOfMonthType } from 'domain-models/Dividends/DayOfMonthType.enum';
import { EnumHelper } from '@singularsystems/neo-core';
import { Months } from '../../../Models/Months';
import { DateCycleType } from '../../../Models/Dividends/DateCycleType';

export interface IDateCycleEditComponentProps {
    viewModel: DateCyclesVM;
    dateCycle?: DateCycle;
    inModal?: boolean;
}

@observer
export class DateCycleEditComponent extends React.Component<IDateCycleEditComponentProps> {

    public render() {

        let { viewModel, dateCycle, inModal } = this.props;
        dateCycle = dateCycle ?? viewModel.editingDateCycle ?? undefined;

        if (!dateCycle) return null;

        if (inModal) {
            return (
                <>
                    {this.renderHeader(dateCycle)}
                    {this.renderCycleDates(viewModel, dateCycle)}
                </>
            )
        } else {
            return (
                <Neo.Form model={dateCycle} showSummaryModal allReadOnly={viewModel.isReadOnly} onSubmit={() => viewModel.saveDateCycle()}>
                    <div className="d-flex justify-content-between mb-4">
                        <Neo.Button icon="arrow-left" variant="light" className="min-width" onClick={() => viewModel.editingDateCycle = null}>Back</Neo.Button>
                        <Neo.Button isSubmit icon="save" pulse={dateCycle.readyToSave}>Save</Neo.Button>
                    </div>

                    <Neo.GridLayout>
                        {this.renderHeader(dateCycle)}
                    </Neo.GridLayout>
                    
                    {this.renderCycleDates(viewModel, dateCycle)}
                </Neo.Form>
            )
        }
    }

    private renderHeader(dateCycle: DateCycle) {
        return (
            <div>
                
                <Neo.FormGroupInline readOnly={false} bind={dateCycle.meta.dateCycleName} />
                <Neo.FormGroupInline readOnly={false} bind={dateCycle.meta.description} input={{rows: 3}} />
                <Neo.FormGroupInline 
                    label="Day of month"
                    bind={dateCycle.meta.dayOfMonthType}
                    select={{ items: EnumHelper.asList(DayOfMonthType), onItemSelected: () => dateCycle.dates.forEach(d => d.onDayOfMonthTypeChanged()) }}
                    footerText="Dates below will default to this day of the month." />
                {!this.props.viewModel.isInvestmentSpecific && 
                    <>
                        <Neo.FormGroupInline 
                            label="Allow selection for"
                            readOnly={false}
                            bind={dateCycle.meta.dateCycleType} 
                            select={{items: EnumHelper.asList(DateCycleType)}}
                            footerText="Determines if this date cycle can be selected for compounding, dividend cycles, rate frequency, or all of the above." />
                        <Neo.FormGroupInline
                            readOnly={false}
                            bind={dateCycle.meta.ordinal} />
                    </>}
                {this.props.viewModel.isInvestmentSpecific &&
                    <Neo.FormGroupInline 
                        label="Specific to investment?" 
                        data-tip="Un-tick if you want this date schedule to be available for selection in other investments."
                        bind={dateCycle.meta.forSingleInvestment} />}
            </div>)
    }

    private renderCycleDates(viewModel: DateCyclesVM, dateCycle: DateCycle) {
        return (
            dateCycle.dayOfMonthType && 
                <div className="mt-4">
                    <NeoGrid.Grid items={dateCycle.dates}
                        showAddButton={!viewModel.isReadOnly}
                        footerButtons={viewModel.isReadOnly ? undefined : () => this.getGridFooterButtons(viewModel, dateCycle)}>
                        {(item, meta) => (
                            <NeoGrid.Row>
                                <NeoGrid.Column 
                                    bind={meta.month}
                                    select={{ items: EnumHelper.asList(Months), sortByValue: true }} />
                                
                                <NeoGrid.Column 
                                    label="Day of month (override)" 
                                    bind={meta.dayOfMonth}
                                    select={{ items: EnumHelper.asList(DayOfMonthType), onItemSelected: () => item.onDayOfMonthTypeChanged() }} />

                                <NeoGrid.Column 
                                    label="Day of month" 
                                    bind={meta.dayOfMonthValue}
                                    select={{ items: EnumHelper.asList(DayOfMonthType), renderAsText: true }} />
                                
                                <NeoGrid.Column 
                                    bind={meta.day} 
                                    disabled={item.dayOfMonthValue !== DayOfMonthType.Custom} />

                                <NeoGrid.ButtonColumn deleteButton={{ disabled: viewModel.isReadOnly }} />
                            </NeoGrid.Row>
                        )}
                    </NeoGrid.Grid>
                </div>
        )
    }

    private getGridFooterButtons(viewModel: DateCyclesVM, DateCycle: DateCycle) {
        if (DateCycle.dates.length === 0) {
            return <Neo.Button className="ms-2" icon="calendar" onClick={() => viewModel.addMonths()}>
                    Add all months
                </Neo.Button>
        }
        return null;
    }
}